import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { useEpisode } from 'context/episode';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { updateReview } from 'services/api/reviews';
import colors from 'styles/theme/colors';

type DenyReferralModalProps = {
  handleClose: () => void;
  patientName: string;
  locationEpisode: LocationEpisode;
  review: Referral;
};

const DenyReferralModal = ({ handleClose, patientName, locationEpisode, review }: DenyReferralModalProps) => {
  const [denialReason, setDenialReason] = useState<{ apiName: string; displayName: string }>();
  const { profile } = useProfile();
  const { refetch } = useEpisode();
  const { note, setNote } = useActivityInput();
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const invalidateQueries = useInvalidateQueries();
  const navigate = useNavigate();
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: async () => {
      try {
        await refetch({ throwOnError: true });

        invalidateQueries({
          episode: { id: locationEpisode.episodeId },
          locationEpisode: { id: locationEpisode.id },
          referrals: true,
          referral: { id: review.id },
          attachmentsCount: true,
        });
      } catch {
        navigate(`/referrals/${review.id}`);
      }

      handleClose();
    },
    onSettled: () => {
      setAttachmentUploading(false);
    },
  });

  const handleSubmit = () => {
    delete note?.escalationType;

    mutate({
      id: review.id,
      status: ReviewStatus.REJECTED,
      denialReasonApiName: denialReason?.apiName,
      reviewedById: profile?.id,
      note: note
        ? {
            ...note,
            attachments: note.attachments?.map((upload) => upload.serialize()),
          }
        : undefined,
    });
  };

  return (
    <StandardModal title='Deny Referral' onCancel={handleClose} colorTheme={ColorThemeName.PrimaryRed}>
      <StandardModal.PatientName>Patient: {patientName}</StandardModal.PatientName>
      <StandardModal.SubHeader>
        Denying this referral will notify the co-management team and will remove <b>{review.group?.name}</b> from the
        patient.
      </StandardModal.SubHeader>

      <InputGroup>
        <InputContainer>
          <label>Denial Reason</label>
          <Select
            options={review.denialReasonOptions}
            onChange={(val) => setDenialReason(val)}
            getOptionValue={(prop) => prop.apiName}
            getOptionLabel={(prop) => prop.displayName}
            value={denialReason}
            placeholder='Select a denial reason'
          />
        </InputContainer>
        <InputContainer>
          <label>Provide any additional notes for denial reason</label>
          <ActivityInput
            locationEpisodeId={locationEpisode.id}
            values={note}
            setValues={setNote}
            onUploading={setAttachmentUploading}
          />
        </InputContainer>
      </InputGroup>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='danger' disabled={!denialReason || attachmentUploading || isPending} onClick={handleSubmit}>
          Deny Referral
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
};

export default DenyReferralModal;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: ${colors.black75};
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 36px;
`;
